import {
  collection,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { normalizeClassStyleDefaults } from "./classStyleDefaults";
import { readCollectionServerPreferred } from "./firestoreCacheFirstRead";
import { stripUndefinedDeep } from "./stripUndefinedDeep";

const BATCH_LIMIT = 450;

const classStyleSchemesCollection = (uid) =>
  collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.CLASS_STYLE_SCHEMES);

const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export const normalizeClassStyleSchemeDocument = (scheme = {}) => {
  const id = String(scheme?.id || "").trim();
  if (!id) return null;

  return {
    id,
    name: String(scheme.name || scheme.label || "Untitled Style").trim().slice(0, 20) || "Untitled Style",
    styleDefaults:
      scheme.styleDefaults && typeof scheme.styleDefaults === "object" && !Array.isArray(scheme.styleDefaults)
        ? scheme.styleDefaults
        : {},
    createdAt: scheme.createdAt || null,
    updatedAt: scheme.updatedAt || null,
  };
};

export const enrichClassStyleSchemesForUi = (schemes = [], user = {}) =>
  (Array.isArray(schemes) ? schemes : [])
    .map((scheme) => normalizeClassStyleSchemeDocument(scheme))
    .filter(Boolean)
    .filter((scheme) => scheme.styleDefaults && Object.keys(scheme.styleDefaults).length > 0)
    .map((scheme) => ({
      ...scheme,
      styleDefaultsRaw: scheme.styleDefaults,
      styleDefaults: normalizeClassStyleDefaults({ classStyleDefaults: scheme.styleDefaults }, user),
    }));

export const fetchClassStyleSchemes = async (uid) => {
  if (!uid) return [];

  const snapshot = await readCollectionServerPreferred(classStyleSchemesCollection(uid));
  return snapshot.docs
    .map((schemeDoc) =>
      normalizeClassStyleSchemeDocument({
        ...schemeDoc.data(),
        id: schemeDoc.data()?.id || schemeDoc.id,
      })
    )
    .filter(Boolean);
};

const toPersistedSchemes = (schemes = []) =>
  (Array.isArray(schemes) ? schemes : [])
    .map((scheme) =>
      normalizeClassStyleSchemeDocument({
        ...scheme,
        styleDefaults: scheme.styleDefaultsRaw || scheme.styleDefaults,
      })
    )
    .filter(Boolean);

export const mergeClassStyleSchemeIntoList = (schemes = [], savedScheme = null) => {
  const saved = normalizeClassStyleSchemeDocument(savedScheme);
  if (!saved) {
    return Array.isArray(schemes) ? schemes.slice() : [];
  }

  const list = Array.isArray(schemes) ? schemes.slice() : [];
  const existingIndex = list.findIndex((scheme) => String(scheme?.id || "").trim() === saved.id);
  if (existingIndex === -1) {
    list.push(saved);
    return list;
  }

  list[existingIndex] = saved;
  return list;
};

export const upsertClassStyleScheme = async (uid, scheme = {}) => {
  if (!uid) {
    throw new Error("Missing user id for class style schemes.");
  }

  const normalizedScheme = normalizeClassStyleSchemeDocument({
    ...scheme,
    styleDefaults: scheme.styleDefaultsRaw || scheme.styleDefaults,
  });
  if (!normalizedScheme) {
    throw new Error("Missing class style scheme id.");
  }

  const persistedScheme = stripUndefinedDeep(normalizedScheme);
  await setDoc(doc(classStyleSchemesCollection(uid), persistedScheme.id), persistedScheme);
  return persistedScheme;
};

/** @internal Exported for regression tests. */
export const getClassStyleSchemeDeleteDocIds = (existingDocs = [], nextSchemes = []) => {
  const nextIds = new Set(
    (Array.isArray(nextSchemes) ? nextSchemes : [])
      .map((scheme) => String(scheme?.id || "").trim())
      .filter(Boolean)
  );

  return (Array.isArray(existingDocs) ? existingDocs : [])
    .filter((schemeDoc) => {
      const docId = String(schemeDoc?.id || "").trim();
      const dataId = String(schemeDoc?.data?.()?.id || "").trim();
      return docId && !nextIds.has(docId) && (!dataId || !nextIds.has(dataId));
    })
    .map((schemeDoc) => schemeDoc.id);
};

/** @internal Exported for regression tests. */
export const getClassStyleSchemeDocIdsToDelete = (existingDocs = [], schemeId) => {
  const targetId = String(schemeId || "").trim();
  if (!targetId) {
    return [];
  }

  const matchedIds = (Array.isArray(existingDocs) ? existingDocs : [])
    .filter((schemeDoc) => {
      const docId = String(schemeDoc?.id || "").trim();
      const dataId = String(schemeDoc?.data?.()?.id || "").trim();
      return docId === targetId || dataId === targetId;
    })
    .map((schemeDoc) => String(schemeDoc.id));

  if (!matchedIds.includes(targetId)) {
    matchedIds.push(targetId);
  }

  return [...new Set(matchedIds.filter(Boolean))];
};

const commitSchemeUpserts = async (colRef, normalizedSchemes) => {
  for (const schemeItems of chunk(normalizedSchemes, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    schemeItems.forEach((scheme) => {
      batch.set(doc(colRef, scheme.id), scheme);
    });
    await batch.commit();
  }
};

const commitSchemeDeletes = async (colRef, deleteDocIds = []) => {
  for (const idChunk of chunk(deleteDocIds, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    idChunk.forEach((schemeDocId) => {
      batch.delete(doc(colRef, schemeDocId));
    });
    await batch.commit();
  }
};

export const replaceClassStyleSchemes = async (uid, schemes = []) => {
  if (!uid) {
    throw new Error("Missing user id for class style schemes.");
  }

  const normalizedSchemes = toPersistedSchemes(schemes);
  const colRef = classStyleSchemesCollection(uid);
  const existingSnapshot = await readCollectionServerPreferred(colRef);
  const deleteDocIds = getClassStyleSchemeDeleteDocIds(existingSnapshot.docs, normalizedSchemes);

  await commitSchemeUpserts(colRef, normalizedSchemes);
  await commitSchemeDeletes(colRef, deleteDocIds);

  return normalizedSchemes;
};

export const deleteClassStyleSchemeById = async (uid, schemeId) => {
  if (!uid) {
    throw new Error("Missing user id for class style schemes.");
  }

  const targetId = String(schemeId || "").trim();
  if (!targetId) {
    throw new Error("Missing class style scheme id.");
  }

  const colRef = classStyleSchemesCollection(uid);
  const existingSnapshot = await readCollectionServerPreferred(colRef);
  const deleteDocIds = getClassStyleSchemeDocIdsToDelete(existingSnapshot.docs, targetId);
  await commitSchemeDeletes(colRef, deleteDocIds);

  return existingSnapshot.docs
    .filter((schemeDoc) => !deleteDocIds.includes(schemeDoc.id))
    .map((schemeDoc) =>
      normalizeClassStyleSchemeDocument({
        ...schemeDoc.data(),
        id: schemeDoc.data()?.id || schemeDoc.id,
      })
    )
    .filter(Boolean);
};
